"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";

// Import extracted auth components
import { SignInForm } from "@/components/auth/SignInForm";
import { SignUpForm } from "@/components/auth/SignUpForm";

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultMode?: "sign-in" | "sign-up";
}

export function AuthModal({
  open,
  onOpenChange,
  defaultMode = "sign-in",
}: AuthModalProps) {
  const t = useTranslations("AuthModal");
  const tCommon = useTranslations("Common");
  const [activeTab, setActiveTab] = useState<"sign-up" | "sign-in">(
    defaultMode,
  );
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevDefaultMode, setPrevDefaultMode] = useState(defaultMode);

  // Reset tab when modal opens or when the requested mode changes while open
  if (open !== prevOpen || defaultMode !== prevDefaultMode) {
    setPrevOpen(open);
    setPrevDefaultMode(defaultMode);
    if (open) {
      setActiveTab(defaultMode);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg p-0 overflow-hidden gap-0 border-0 bg-white dark:bg-gray-900"
        showCloseButton={true}
      >
        <DialogTitle className="sr-only">{t("title")}</DialogTitle>
        <DialogDescription className="sr-only">
          {activeTab === "sign-in"
            ? t("signInDescription")
            : t("signUpDescription")}
        </DialogDescription>
        <div className="w-full p-8 pt-14 pb-12 flex flex-col justify-center bg-white dark:bg-gray-900">
            <Tabs
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as "sign-up" | "sign-in")}
              className="w-full h-full flex flex-col"
            >
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-transparent gap-4 h-auto p-0">
                <TabsTrigger
                  value="sign-in"
                  className={cn(
                    "py-2.5 rounded-lg border data-[state=active]:shadow-none transition-all",
                    activeTab === "sign-in"
                      ? "border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30"
                      : "border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600",
                  )}
                >
                  {tCommon("auth.signIn")}
                </TabsTrigger>
                <TabsTrigger
                  value="sign-up"
                  className={cn(
                    "py-2.5 rounded-lg border data-[state=active]:shadow-none transition-all",
                    activeTab === "sign-up"
                      ? "border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30"
                      : "border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600",
                  )}
                >
                  {tCommon("auth.signUp")}
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="sign-up"
                className="mt-0 flex-1 flex flex-col"
              >
                <SignUpForm onSuccess={() => onOpenChange(false)} />
              </TabsContent>

              <TabsContent
                value="sign-in"
                className="mt-0 flex-1 flex flex-col"
              >
                <SignInForm onSuccess={() => onOpenChange(false)} />
              </TabsContent>
            </Tabs>
          </div>
      </DialogContent>
    </Dialog>
  );
}
